"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.loadWeave = loadWeave;
exports.saveWeave = saveWeave;
exports.saveDocs = saveDocs;
const path = __importStar(require("path"));
const fs = __importStar(require("fs-extra"));
const frontmatterLoader_1 = require("../serializers/frontmatterLoader");
const frontmatterSaver_1 = require("../serializers/frontmatterSaver");
const pathUtils_1 = require("../utils/pathUtils");
const threadRepository_1 = require("./threadRepository");
async function loadWeave(loomRoot, weaveId, index, overrideWeavePath) {
    const weavePath = overrideWeavePath ?? path.join(loomRoot, 'loom', weaveId);
    if (!await fs.pathExists(weavePath)) {
        throw new Error(`Weave directory not found: ${weavePath}`);
    }
    // Load threads
    const threadIds = await (0, pathUtils_1.listThreadDirs)(weavePath);
    const threads = [];
    for (const threadId of threadIds) {
        const threadPath = path.join(weavePath, threadId);
        threads.push(await (0, threadRepository_1.loadThread)(loomRoot, weaveId, threadId, index, threadPath));
    }
    // Load weave-root docs: .md files directly at weave root.
    // (NB: a "loose fiber" is a graph position — a doc with no parent and no children —
    // NOT a location. This field holds weave-root docs; the two are unrelated concepts.)
    const looseFibers = [];
    const rootEntries = await fs.readdir(weavePath, { withFileTypes: true });
    for (const entry of rootEntries) {
        if (!entry.isFile() || !entry.name.endsWith('.md'))
            continue;
        try {
            looseFibers.push(await (0, frontmatterLoader_1.loadDoc)(path.join(weavePath, entry.name)));
        }
        catch (e) {
            if (e instanceof frontmatterLoader_1.FrontmatterParseError) {
                console.warn(`Skipping ${entry.name}: ${e.message}`);
            }
            else
                throw e;
        }
    }
    // Load weave-level chats from chats/
    const chats = [];
    const chatsDir = path.join(weavePath, 'chats');
    if (await fs.pathExists(chatsDir)) {
        const chatFiles = (await fs.readdir(chatsDir)).filter(f => f.endsWith('.md'));
        for (const f of chatFiles) {
            try {
                const doc = await (0, frontmatterLoader_1.loadDoc)(path.join(chatsDir, f));
                if (doc.type === 'chat')
                    chats.push(doc);
            }
            catch (e) {
                console.warn(`Skipping ${f}: ${e.message}`);
            }
        }
    }
    // Load weave-level reference docs from refs/
    const refDocs = [];
    const refsDir = path.join(weavePath, 'refs');
    if (await fs.pathExists(refsDir)) {
        const refFiles = (await fs.readdir(refsDir)).filter(f => f.endsWith('.md'));
        for (const f of refFiles) {
            try {
                refDocs.push(await (0, frontmatterLoader_1.loadDoc)(path.join(refsDir, f)));
            }
            catch (e) {
                console.warn(`Skipping ${f}: ${e.message}`);
            }
        }
    }
    const allDocs = [
        ...threads.flatMap(t => t.allDocs),
        ...looseFibers,
        ...chats,
        ...refDocs,
    ];
    return { id: weaveId, threads, looseFibers, chats, refDocs, allDocs };
}
async function saveWeave(loomRoot, weave) {
    const weavePath = path.join(loomRoot, 'loom', weave.id);
    for (const thread of weave.threads) {
        await (0, threadRepository_1.saveThread)(loomRoot, weave.id, thread);
    }
    for (const fiber of weave.looseFibers) {
        const filePath = fiber._path ?? path.join(weavePath, `${fiber.id}.md`);
        await (0, frontmatterSaver_1.saveDoc)(fiber, filePath);
    }
    for (const chat of weave.chats) {
        const filePath = chat._path ?? path.join(weavePath, 'chats', `${chat.id}.md`);
        await (0, frontmatterSaver_1.saveDoc)(chat, filePath);
    }
}
/**
 * Persist ONLY the documents whose ids are in `docIds`, leaving every other
 * doc in the weave untouched on disk. This is the single-event save path:
 * `runEvent` passes the `changed` set from `applyEvent` so a workflow event
 * rewrites exactly the docs it mutated — bounding the blast radius (a
 * non-idempotent save path can never corrupt an unchanged sibling) and
 * eliminating spurious re-serialisation churn. `saveWeave` remains for genuine
 * bulk operations (migrations). See loom/core-engine/event-save-scope.
 */
async function saveDocs(loomRoot, weave, docIds) {
    const wanted = new Set(docIds);
    if (wanted.size === 0)
        return;
    const weavePath = path.join(loomRoot, 'loom', weave.id);
    for (const thread of weave.threads) {
        const threadPath = path.join(weavePath, thread.id);
        for (const doc of thread.allDocs) {
            if (!wanted.has(doc.id))
                continue;
            const filePath = doc._path ?? (0, threadRepository_1.docPathInThread)(doc, threadPath, thread.id);
            await (0, frontmatterSaver_1.saveDoc)(doc, filePath);
        }
    }
    for (const fiber of weave.looseFibers) {
        if (!wanted.has(fiber.id))
            continue;
        const filePath = fiber._path ?? path.join(weavePath, `${fiber.id}.md`);
        await (0, frontmatterSaver_1.saveDoc)(fiber, filePath);
    }
    for (const chat of weave.chats) {
        if (!wanted.has(chat.id))
            continue;
        const filePath = chat._path ?? path.join(weavePath, 'chats', `${chat.id}.md`);
        await (0, frontmatterSaver_1.saveDoc)(chat, filePath);
    }
}
